// src/components/tokenPage/risk/RollingVolatilityCard.tsx

import React, { useMemo, useState } from "react";
import { TimeSeriesChart } from "@/components/charts/base";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { DataCard } from "@/components/ui";
import { useTokenPrice } from "@/contexts/token";

type Range = '90d' | '180d' | '365d' | 'All';

const RANGE_TO_DAYS: Record<Range, number> = { '90d': 90, '180d': 180, '365d': 365, 'All': Infinity };

const RollingVolatilityCard: React.FC = () => {
  const [range, setRange] = useState<Range>('365d');
  const [win, setWin] = useState<30|90|180|365>(90);
  
  const { token, isLoading } = useTokenPrice();
  const prices = token.prices;

  const { dates, priceValues } = useMemo(() => {
    const d = prices.dates;
    const values = prices.values;
    
    if (d.length === 0 || values.length === 0) {
      return { dates: [], priceValues: [] };
    }
    
    const maxPoints = RANGE_TO_DAYS[range];
    const start = maxPoints === Infinity ? 0 : Math.max(0, d.length - maxPoints);
    const datesSliced = d.slice(start);
    const pricesSliced = values.slice(start);
    
    // Find first valid
    const firstValidIdx = pricesSliced.findIndex(v => Number.isFinite(v));
    if (firstValidIdx >= 0) {
      return { 
        dates: datesSliced.slice(firstValidIdx), 
        priceValues: pricesSliced.slice(firstValidIdx),
      };
    }
    return { dates: datesSliced, priceValues: pricesSliced };
  }, [prices, range]);

  // Hide if no data available (after loading)
  if (!isLoading && (!priceValues.length || priceValues.every(v => !Number.isFinite(v)))) {
    return null;
  }

  return (
    <DataCard
      title="Rolling Realized Volatility"
      subtitle="30d, 90d, 180d, 365d windows"
      icon="activity"
      iconColor="text-sky-400"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400 mr-1">Window</span>
          <button className={`text-xs px-2 py-1 rounded-md ${win===30?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} onClick={() => setWin(30)}>30d</button>
          <button className={`text-xs px-2 py-1 rounded-md ${win===90?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} onClick={() => setWin(90)}>90d</button>
          <button className={`text-xs px-2 py-1 rounded-md ${win===180?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} onClick={() => setWin(180)}>180d</button>
          <button className={`text-xs px-2 py-1 rounded-md ${win===365?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} onClick={() => setWin(365)}>365d</button>
          <span className="text-[11px] text-slate-500 mx-1">|</span>
          <TimeRangePicker options={['90d','180d','365d','All'] as const} value={range} onChange={setRange} theme="blue" />
        </div>
      }
    >
      <TimeSeriesChart
        dates={dates}
        series={[{
          name: `Vol ${win}d`,
          values: priceValues,
          color: '#38bdf8',
          areaStyle: true,
        }]}
        height={400}
        transform="volatility"
        window={win}
        yAxisFormat="percent"
        showMeanBands
      />
    </DataCard>
  );
};

export default RollingVolatilityCard;
